import { useCallback, useMemo } from 'react';

import defaultChatSkillsBundle from '../../copied-from-backend/defaultChatSkills.json';
// eslint-disable-next-line import/no-cycle
import { globalState } from '../models';
import {
  type ChatSkill,
  type DefaultChatSkill,
  ChatSkillScope,
  isChatSkillDeletionMarker,
} from './types';

// Explanation: the JSON file is generated from the backend YAML at build
// time (shared/build/yamlToJson.mjs); TypeScript widens its scopes/status
// strings instead of inferring them as ChatSkillScope/ChatSkillStatus, so
// we declare the documented shape here and let the JSON values flow in.
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const bundle = defaultChatSkillsBundle as { data: DefaultChatSkill[]; };
export const DEFAULT_CHAT_SKILLS: ReadonlyArray<DefaultChatSkill> = bundle.data;

const DEFAULT_CHAT_SKILL_SLUGS: ReadonlySet<string> = new Set(DEFAULT_CHAT_SKILLS.map((d) => d.slug));

function chatSkillFromDefault(def: DefaultChatSkill): ChatSkill {
  return {
    slug: def.slug,
    name: def.name,
    description: def.description,
    instructions: def.instructions,
    scopes: def.scopes,
    keywords: def.keywords,
    isEnabled: true,
    isDefault: true,
  };
}

export function useChatSkills(): ChatSkill[] {
  const [overrides, created] = globalState(
    useCallback(
      (state) => [
        state.persistent.settings.overriddenChatSkills,
        state.persistent.settings.createdChatSkills,
      ],
      [],
    ),
  );
  return useMemo(() => {
    const result: ChatSkill[] = [];
    for (const def of DEFAULT_CHAT_SKILLS) {
      const override = overrides?.[def.slug];
      if (isChatSkillDeletionMarker(override)) {
        continue;
      }
      if (override) {
        result.push({ keywords: def.keywords, ...override, isDefault: true });
        continue;
      }
      result.push(chatSkillFromDefault(def));
    }
    if (overrides) {
      // Overrides are stored as complete skill objects, so a customized copy of a
      // since-retired default keeps working after the built-in is removed.
      for (const [slug, override] of Object.entries(overrides)) {
        if (DEFAULT_CHAT_SKILL_SLUGS.has(slug) || isChatSkillDeletionMarker(override)) {
          continue;
        }
        result.push({ ...override, isDefault: true });
      }
    }
    if (created) {
      for (const skill of created) {
        result.push({ ...skill, isDefault: false });
      }
    }
    return result;
  }, [overrides, created]);
}

export function chatSkillMatchesScope(skill: ChatSkill, scope: ChatSkillScope): boolean {
  return skill.scopes.includes(scope);
}

const DEFAULT_TAG_SKILL_NAME = DEFAULT_CHAT_SKILLS.find((d) => d.slug === 'tag-documents')?.name;

/**
 * Display-only "🔖 ... (new)" variant so the default tag skill is
 * distinguishable next to a customized V1 "🏷️ Tag the document" preset
 * (RW-45946). Applied at render time only -- baking it into the ChatSkill
 * would let settings flows persist it into overriddenChatSkills. A skill
 * the user renamed keeps their name (it won't match the default).
 */
export function chatSkillDisplayName(skill: ChatSkill, hasCustomV1TagPrompt: boolean): string {
  if (hasCustomV1TagPrompt && skill.slug === 'tag-documents' && skill.name === DEFAULT_TAG_SKILL_NAME) {
    return `${skill.name.replace('🏷️', '🔖')} (new)`;
  }
  return skill.name;
}
